import { Header } from "@/components/header";
import { WorkspaceScrollArea } from "@papier/ui/components/ui/workspace-sidebar";
import { useCdnAccess } from "@/hooks/use-cdn-access";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate, Outlet, redirect, useMatch } from "@tanstack/react-router";
import { useRef } from "react";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
  head: () => ({
    meta: [{ title: "Papier" }],
  }),
  beforeLoad: async ({ context: { queryClient, authOptions }, location }) => {
    const [session, hasOrgs] = await Promise.all([
      queryClient.fetchQuery(authOptions.session),
      queryClient.fetchQuery(authOptions.hasOrganizations),
    ]);

    if (!session) {
      throw redirect({
        to: "/auth/$authView",
        params: { authView: "sign-in" },
        search: {
          redirectTo: location.pathname !== "/" ? location.pathname : undefined,
        },
      });
    }

    if (!hasOrgs || !session.session.activeOrganizationId) {
      throw redirect({
        to: "/auth/$authView",
        params: { authView: "organization" },
        search: {
          redirectTo: location.pathname !== "/" ? location.pathname : undefined,
        },
      });
    }
  },
});

function AppLayout() {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const editorMatch = useMatch({
    from: "/_app/projects/$projectSlug/templates/$templateSlug/edit",
    shouldThrow: false,
    select: (match) => match.id,
  });

  const { authOptions } = Route.useRouteContext();
  const { data: session } = useSuspenseQuery(authOptions.session);

  useCdnAccess(session?.session.activeOrganizationId);

  if (!session) {
    return <Navigate to="/auth/$authView" params={{ authView: "sign-in" }} />;
  }

  return (
    <div className="flex max-h-svh min-h-svh w-full flex-col bg-content-background dark:bg-background">
      {!editorMatch && <Header scrollContainer={scrollContainer} />}
      <div className="flex flex-1 overflow-hidden">
        <WorkspaceScrollArea ref={scrollContainer}>
          <Outlet />
        </WorkspaceScrollArea>
      </div>
    </div>
  );
}
